import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, rp } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BranchFilter, useBranchFilter } from "@/admin/BranchFilter";
import { canSee } from "@/admin/RoleGuard";
import AnalyticsPanel from "@/admin/AnalyticsPanel";
import { DailySchedules } from "@/admin/dashboard/DailySchedules";
import { Card, FinancePanel, HubConflicts, HubsOverview, PartnerPanel, Timeline }
  from "@/admin/dashboard/RoleWidgets";

const ROLE_TITLE = {
  owner: "Dashboard Owner", admin: "Dashboard Operasional", partner: "Dashboard Hub",
  finance: "Dashboard Keuangan", driver: "Dashboard Driver",
};
const ROLE_HINT = {
  owner: "Semua Hub, operasional dan uang dalam satu halaman.",
  admin: "Tugas operasional Hub yang ditugaskan hari ini.",
  partner: "Hanya Hub yang ditugaskan ke akun Anda.",
  finance: "Pembayaran, deposit, refund dan komisi.",
  driver: "Serah terima dan pengembalian hari ini.",
};

export default function Dashboard() {
  const { user } = useAuth();
  const role = user?.role || "";
  const [d, setD] = useState(null);
  const [s, setS] = useState(null);
  const { branches, branchId, setBranchId } = useBranchFilter();
  const showOps = ["owner", "admin", "partner", "driver"].includes(role);
  const showLegacy = role === "owner";

  const load = useCallback(() => {
    api.get("/dashboard", { params: { hub_id: branchId || undefined } })
      .then((r) => setD(r.data)).catch(() => setD({ cards: [], action_center: [] }));
  }, [branchId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!showLegacy) return;
    api.get("/stats", { params: { branch_id: branchId || undefined } })
      .then((r) => setS(r.data)).catch(() => {});
  }, [branchId, showLegacy]);

  if (!d) return <div className="text-ink-soft">Loading…</div>;

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl tracking-tight" data-testid="dashboard-title">
            {ROLE_TITLE[role] || "Dashboard"}
          </h1>
          <p className="mt-1 text-xs text-ink-soft">{ROLE_HINT[role]}</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {role !== "partner" && (
            <BranchFilter branches={branches} value={branchId} onChange={setBranchId}
              testid="dashboard-branch-filter" />
          )}
          {role === "partner" ? (
            <Link to="/admin/partner" data-testid="quick-venue-booking"
              className="lm-btn-primary rounded-md px-4 py-2.5 text-sm font-semibold">+ Booking di Venue</Link>
          ) : canSee(user, "bookings/new") && (
            <Link to="/admin/bookings/new" data-testid="quick-new-booking"
              className="lm-btn-primary rounded-md px-4 py-2.5 text-sm font-semibold">+ New Booking</Link>
          )}
          {canSee(user, "bookings") && (
            <Link to="/admin/bookings" data-testid="quick-bookings"
              className="rounded-md px-4 py-2.5 text-sm border border-black/15">Bookings</Link>
          )}
          {canSee(user, "calendar") && (
            <Link to="/admin/calendar" data-testid="quick-calendar"
              className="rounded-md px-4 py-2.5 text-sm border border-black/15">Calendar</Link>
          )}
          {role === "admin" && (
            <Link to="/admin/scan" data-testid="quick-unit-check"
              className="rounded-md border border-black/15 px-4 py-2.5 text-sm">
              Cek Unit &amp; Cek Cepat
            </Link>
          )}
        </div>
      </div>

      {d.cards.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="dash-cards">
          {d.cards.map((c) => <Card key={c.key} card={c} />)}
        </div>
      )}

      {d.hub_conflicts && <HubConflicts rows={d.hub_conflicts} />}

      {role === "admin" && !(user?.hub_branch_ids || []).length && (
        <div data-testid="admin-missing-hub-alert"
          className="border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
          Akun ini belum ditugaskan ke Hub. Minta Owner memilih Hub Anda di Staff Accounts.
        </div>
      )}

      {role === "admin" && (
        <DailySchedules bookings={d.today_bookings || []} returns={d.today_returns || []} />
      )}

      {role === "partner" && (
        <PartnerPanel hubs={d.hubs || []} commissions={d.commissions || { pending: 0, earned: 0 }}
          percent={d.commission_percent || 0} />
      )}

      {showOps && <Timeline rows={d.timeline || []} />}

      {d.units && (
        <section data-testid="dash-units">
          <h2 className="text-base md:text-lg font-semibold mb-3">Unit</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card card={{ key: "unit-total", label: "Total unit", value: d.units.total }} />
            <Card card={{ key: "unit-available", label: "Siap disewa", value: d.units.available }} />
            <Card card={{ key: "unit-rented", label: "Sedang disewa", value: d.units.rented }} />
            <Card card={{ key: "unit-maintenance", label: "Maintenance", value: d.units.maintenance,
              tone: "danger" }} />
          </div>
        </section>
      )}

      {d.finance && <FinancePanel fin={d.finance} />}
      {d.hubs_overview && <HubsOverview rows={d.hubs_overview} />}

      {showLegacy && <AnalyticsPanel branchId={branchId} />}

      {showLegacy && s && (
        <section data-testid="dash-business">
          <h2 className="text-base md:text-lg font-semibold mb-3">Bisnis</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card card={{ key: "bookings-month", label: "Booking bulan ini", value: s.bookings_this_month }} />
            <Card card={{ key: "revenue-month", label: "Revenue bulan ini", value: s.revenue_this_month, money: true }} />
            <Card card={{ key: "customers", label: "Total customer", value: s.total_customers }} />
            <Card card={{ key: "top-source", label: "Sumber teratas", value: s.top_source }} />
          </div>
        </section>
      )}

      {role === "finance" && (
        <p className="text-xs text-ink-soft" data-testid="finance-note">
          Serah terima unit dan inspeksi ditangani tim operasional — peran Finance tidak mengubah
          status booking. Nilai di atas: {rp((d.finance || {}).paid_today_amount || 0)} masuk hari ini.
        </p>
      )}
    </div>
  );
}
